"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.promoteToDesign = promoteToDesign;
const path = __importStar(require("path"));
const dist_1 = require("../../core/dist");
const aiSummarization_1 = require("./utils/aiSummarization");
const SYSTEM_PROMPT = `You are an AI assistant embedded in REslava Loom, a document-driven workflow system.
Your task: read the provided document and produce a design doc that formalizes the idea or conversation.
Respond with exactly this format — nothing else before or after:

TITLE: <one concise line describing the design>

## Goal
<what this design achieves in 1-2 sentences>

## Context
<background, constraints, and motivation>

## Design
<the proposed solution — architecture, key decisions, trade-offs>

## Decisions
<list of concrete decisions made, one per bullet>

## Open questions
<anything still unresolved>`;
async function promoteToDesign(input, deps) {
    const doc = await deps.loadDoc(input.filePath);
    const { weaveId, threadId } = input.targetWeaveId
        ? { weaveId: input.targetWeaveId, threadId: input.targetThreadId }
        : deriveLocation(input.filePath, deps.loomRoot);
    let title;
    let body;
    if (input.body !== undefined) {
        body = input.body;
        title = input.title ?? doc.title;
    }
    else {
        if (!doc.content || doc.content.trim().length === 0) {
            throw new Error(`${doc.type} document is empty.`);
        }
        const label = doc.type === 'chat'
            ? 'chat conversation'
            : `${doc.type} document titled "${doc.title}"`;
        const messages = (0, aiSummarization_1.buildSummarizationMessages)(SYSTEM_PROMPT, label, doc.content);
        const reply = await deps.aiClient.complete(messages);
        ({ title, body } = (0, aiSummarization_1.parseTitleAndBody)(reply));
    }
    const targetDir = threadId
        ? path.join(deps.loomRoot, 'loom', weaveId, threadId)
        : path.join(deps.loomRoot, 'loom', weaveId);
    await deps.fs.ensureDir(targetDir);
    let designFilename;
    let filePath;
    if (threadId) {
        // Thread-level: canonical filename is the flat singleton design.md (one per thread).
        filePath = path.join(targetDir, (0, dist_1.singletonFileName)('design'));
        // Dual-read the singleton guard: refuse if either the flat or legacy design exists.
        const legacyDesign = path.join(targetDir, `${threadId}-design.md`);
        if ((await deps.fs.pathExists(filePath)) || (await deps.fs.pathExists(legacyDesign))) {
            throw new Error(`Thread '${threadId}' already has a design. Refine the existing one instead.`);
        }
    }
    else {
        // Weave-root doc: kebab-of-title
        const existingFiles = await deps.fs.readdir(targetDir).catch(() => []);
        designFilename = generateDesignId(title, weaveId, existingFiles);
        filePath = path.join(targetDir, `${designFilename}.md`);
    }
    const designId = (0, dist_1.generateDocId)('design');
    const frontmatter = (0, dist_1.createBaseFrontmatter)('design', designId, title, doc.id);
    const designDoc = {
        ...frontmatter,
        type: 'design',
        status: 'draft',
        content: input.body !== undefined ? body : `# ${title}\n\n${body}`,
    };
    await deps.saveDoc(designDoc, filePath);
    return { filePath, title };
}
function deriveLocation(filePath, loomRoot) {
    const rel = path.relative(path.join(loomRoot, 'loom'), filePath);
    const parts = rel.split(/[\\/]/);
    if (parts.length < 2)
        throw new Error(`Cannot derive weave from path: ${rel}`);
    const weaveId = parts[0];
    if (parts.length >= 3 && parts[1] === 'chats')
        return { weaveId };
    if (parts.length >= 3)
        return { weaveId, threadId: parts[1] };
    return { weaveId };
}
function generateDesignId(title, weaveId, existingFiles) {
    const kebab = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    const base = `${weaveId}-${kebab}-design`;
    const taken = new Set(existingFiles.map(f => f.replace(/\.md$/, '')));
    if (!taken.has(base))
        return base;
    let n = 2;
    while (taken.has(`${base}-${n}`))
        n++;
    return `${base}-${n}`;
}
